import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Dialog,
  DialogContent,
  Checkbox,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Add, Check, PlaylistAddCheck } from '@mui/icons-material';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import { ActionsButton, SettingsCard } from '../../components/composed';
import { ResourceForm } from '../../components/domain/resource';
import { ApproveOrDeny } from '../../components/domain/object_management';
import CallToAction from '../DataBuilderPage/CallToAction';
import { getCsrfToken } from '../../utils/csrf';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const columnHelper = createColumnHelper();

const columns = [
  columnHelper.display({
    id: 'select',
    enableSorting: false,
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllRowsSelected()}
        indeterminate={table.getIsSomeRowsSelected()}
        onChange={table.getToggleAllRowsSelectedHandler()}
        onClick={(e) => e.stopPropagation()}
        inputProps={{ 'aria-label': 'Select all resources' }}
        size="small"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        disabled={!row.getCanSelect()}
        onChange={row.getToggleSelectedHandler()}
        onClick={(e) => e.stopPropagation()}
        inputProps={{ 'aria-label': `Select resource ${row.original.id}` }}
        size="small"
      />
    ),
  }),
  columnHelper.accessor((row) => row.name ?? '', {
    id: 'name',
    header: 'Name',
    sortingFn: 'alphanumeric',
    cell: (info) => info.getValue() || 'N/A',
  }),
  columnHelper.accessor((row) => row.schedule_url ?? '', {
    id: 'schedule_url',
    header: 'Schedule URL',
    sortingFn: 'alphanumeric',
    cell: (info) => {
      const scheduleUrl = info.getValue();
      if (!scheduleUrl) return '';
      return (
        <a
          href={scheduleUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          style={{
            color: '#1976d2',
            textDecoration: 'none',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.textDecoration = 'underline';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.textDecoration = 'none';
          }}
        >
          {scheduleUrl}
        </a>
      );
    },
  }),
  columnHelper.accessor('is_approved', {
    id: 'is_approved',
    header: 'Verified',
    sortingFn: 'basic',
    cell: (info) => (info.getValue() ? <Check sx={{ color: '#4caf50' }} /> : null),
  }),
];

const Resource = ({ facilityId }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [sorting, setSorting] = useState([{ id: 'name', desc: false }]);
  const [rowSelection, setRowSelection] = useState({});
  const [manageOpen, setManageOpen] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkError, setBulkError] = useState(null);

  const table = useReactTable({
    data: resources,
    columns,
    state: { sorting, rowSelection },
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    enableRowSelection: true,
    getRowId: (row) => String(row.id),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const selectedResourceIds = table
    .getSelectedRowModel()
    .rows.map((row) => row.original.id);

  useEffect(() => {
    if (facilityId) {
      fetchResources();
    }
  }, [facilityId]);

  // Refresh list when navigating back to this page (location key changes)
  useEffect(() => {
    if (facilityId) {
      fetchResources();
    }
  }, [location.key]);

  // Refresh list when window regains focus (user navigates back)
  useEffect(() => {
    const handleFocus = () => {
      if (facilityId) {
        fetchResources();
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [facilityId]);

  const fetchResources = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_BASE_URL}/resources/?facility=${facilityId}`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setResources(data);
    } catch (err) {
      console.error('Error fetching resources:', err);
      setError('Failed to load resources. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddResource = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSaveSuccess = (savedResource) => {
    // Add new resource to the list
    setResources([savedResource, ...resources]);
    // Close modal
    setOpen(false);
  };

  const handleRowClick = (resourceId) => {
    navigate(`/resources/${resourceId}`);
  };

  const handleManageSelected = () => {
    setBulkError(null);
    setManageOpen(true);
  };

  const handleManageClose = () => {
    if (!bulkSubmitting) {
      setManageOpen(false);
      setBulkError(null);
    }
  };

  const handleBulkModeration = async (updates) => {
    try {
      setBulkSubmitting(true);
      setBulkError(null);

      const csrfToken = getCsrfToken();
      const headers = {
        'Content-Type': 'application/json',
      };
      if (csrfToken) {
        headers['X-CSRFToken'] = csrfToken;
      }

      const response = await fetch(`${API_BASE_URL}/resources/bulk/`, {
        method: 'PATCH',
        headers,
        credentials: 'include',
        body: JSON.stringify({ ids: selectedResourceIds, ...updates }),
      });

      if (!response.ok) {
        let message = 'Failed to update the selected resources. Please try again.';
        if (response.status === 403) {
          message = 'You do not have permission to manage resources.';
        } else {
          try {
            const data = await response.json();
            message = data.error || data.detail || message;
          } catch (parseError) {
            // Fall back to the generic message when the body is not JSON
          }
        }
        setBulkError(message);
        return;
      }

      setManageOpen(false);
      setRowSelection({});
      fetchResources();
    } catch (err) {
      console.error('Error updating selected resources:', err);
      setBulkError('Network error. Please check your connection and try again.');
    } finally {
      setBulkSubmitting(false);
    }
  };

  const actionsButton = (
    <ActionsButton
      actions={[
        {
          id: 'add-resource',
          label: 'Add Resource',
          icon: <Add fontSize="small" />,
          onClick: handleAddResource,
        },
        {
          id: 'manage-selected-resources',
          label: 'Manage Selected Resources',
          icon: <PlaylistAddCheck fontSize="small" />,
          disabled: selectedResourceIds.length === 0,
          onClick: handleManageSelected,
        },
      ]}
    />
  );

  if (loading) {
    return (
      <SettingsCard title="Resources" endActions={actionsButton}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </SettingsCard>
    );
  }

  if (error) {
    return (
      <SettingsCard title="Resources" endActions={actionsButton}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        </Box>
      </SettingsCard>
    );
  }

  return (
    <>
      <SettingsCard title="Resources" endActions={actionsButton}>
        {resources.length === 0 ? (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                No resources found.
              </Typography>
            </Box>
            <CallToAction />
          </>
        ) : (
          <>
            <Box sx={{ width: '100%', overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => {
                        const canSort = header.column.getCanSort();
                        const sortDirection = header.column.getIsSorted();
                        return (
                          <th
                            key={header.id}
                            onClick={
                              canSort
                                ? header.column.getToggleSortingHandler()
                                : undefined
                            }
                            style={{
                              textAlign: 'left',
                              paddingRight: 10,
                              borderBottom: `1px solid ${theme.palette.primary.main}`,
                              ...(canSort ? { cursor: 'pointer' } : {}),
                            }}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {sortDirection === 'asc'
                              ? ' \u25B2'
                              : sortDirection === 'desc'
                              ? ' \u25BC'
                              : ''}
                          </th>
                        );
                      })}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row) => (
                    <tr
                      key={row.id}
                      onClick={() => handleRowClick(row.original.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          style={{
                            textAlign: 'left',
                            paddingRight: 10,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                          }}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
            <CallToAction />
          </>
        )}
      </SettingsCard>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogContent sx={{ pt: 3 }}>
          <ResourceForm
            facilityId={facilityId}
            onSaved={handleSaveSuccess}
            showActions={true}
            submitLabel="Submit"
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={manageOpen}
        onClose={handleManageClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent sx={{ p: 0 }}>
          <ApproveOrDeny
            title="Manage selected resources"
            message={`${selectedResourceIds.length} ${
              selectedResourceIds.length === 1 ? 'resource' : 'resources'
            } selected. Approving marks them as verified. Denying removes them from the list.`}
            errorMessage={bulkError}
            disabled={bulkSubmitting}
            onApprove={() => handleBulkModeration({ is_approved: true })}
            onDeny={() => handleBulkModeration({ is_active: false })}
            onCancel={handleManageClose}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Resource;
